import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BrandedVoucherLayout from '@/components/voucher/BrandedVoucherLayout';
import FlightVoucherBody from '@/components/voucher/FlightVoucherBody';
import HotelVoucherBody from '@/components/voucher/HotelVoucherBody';
import { ActivityVoucherBody, TransferVoucherBody, ItineraryVoucherBody } from '@/components/voucher/VoucherBodies';

const API = process.env.REACT_APP_BACKEND_URL;

const SAMPLES = {
  hotel: {
    voucherId: 'VCH-HOT-DEMO-001',
    type: 'hotel',
    status: 'confirmed',
    guestName: 'Rahul & Priya Sharma',
    destination: 'Baku, Azerbaijan',
    hotel: {
      name: 'Four Seasons Hotel Baku',
      address: '1 Neftchilar Avenue, Baku AZ1095',
      city: 'Baku',
      country: 'Azerbaijan',
      stars: 5,
      checkIn: '12 Feb 2026',
      checkOut: '17 Feb 2026',
      nights: 5,
      rooms: 1,
      roomType: 'Deluxe Sea View Suite',
      mealPlan: 'Breakfast Included',
      adults: 2,
      children: 0,
      confirmationNumber: 'FS-BKU-98765432',
      notes: 'Early check-in requested. Flowers + champagne setup for anniversary.',
    },
    publicUrl: 'https://travcations.com/voucher/VCH-HOT-DEMO-001',
  },
  flight: {
    voucherId: 'VCH-FLT-DEMO-001',
    type: 'flight',
    status: 'confirmed',
    guestName: 'Rahul Sharma',
    destination: 'Delhi → Baku',
    flight: {
      from: 'DEL',
      fromCity: 'Indira Gandhi Intl · Delhi',
      to: 'GYD',
      toCity: 'Heydar Aliyev Intl · Baku',
      airline: 'Azerbaijan Airlines',
      flightNumber: 'J2-76',
      cabinClass: 'Business',
      departureDateTime: '12 Feb 2026 · 06:05',
      arrivalDateTime: '12 Feb 2026 · 09:40',
      duration: '4h 35m',
      pnr: 'QR98WB',
      ticketNumber: '257-2147896534',
      seat: '2A',
      baggage: '2 × 32kg check-in, 1 × 10kg cabin',
    },
    publicUrl: 'https://travcations.com/voucher/VCH-FLT-DEMO-001',
  },
  activity: {
    voucherId: 'VCH-ACT-DEMO-001',
    trvRef: 'TRVVCH00007',
    type: 'activity',
    status: 'confirmed',
    guestName: 'Rahul & Priya Sharma',
    destination: 'Baku, Azerbaijan',
    activity: {
      activityName: 'Gobustan + Mud Volcanoes Full Day Tour with Lunch and Heritage Walk',
      venueName: 'Gobustan National Park Visitor Centre',
      address: 'Gobustan Rock Art Cultural Landscape, AZ-5400 Qobustan, Azerbaijan',
      city: 'Baku',
      date: '14 Feb 2026',
      time: '09:00',
      duration: '8 hours',
      voucherType: 'Specific Date Voucher',
      voucherTypeDetails: 'Pre-booked private tour with confirmed time slot. Cannot be exchanged or refunded once redeemed.',
      validity: 'Single use · 14 Feb 2026 only',
      usageValidity: 'Voucher valid only for the booked date. Reschedule allowed up to 24 hours before tour start.',
      openingHours: 'Mon-Sun · 09:00 – 17:00 (last entry 16:00)',
      reportingTime: '09:00 sharp',
      instantConfirmation: 'Yes',
      reservationRequired: 'Yes',
      leadGuestName: 'Rahul Sharma',
      guestNames: ['Rahul Sharma', 'Priya Sharma'],
      totalPax: 2,
      adults: 2,
      partnerRef: 'KLK-AZ-556321',
      bookingReferenceId: 'BMG-558821-A',
      operatorRef: 'AZTOURS-9988',
      overview: 'A full-day private tour to the UNESCO-listed Gobustan Rock Art Landscape and the bubbling mud volcanoes of Azerbaijan. Lunch included at a traditional Azerbaijani restaurant.',
      highlights: ['UNESCO World Heritage rock art', 'Private off-road jeep to mud volcanoes', 'English-speaking certified guide', 'Lunch at a family-run restaurant'],
      inclusions: ['Private car + English-speaking guide', 'Entry tickets to park and Gobustan museum', 'Mineral water', 'Lunch at local restaurant'],
      exclusions: ['Hotel pickup outside Baku city limits', 'Personal expenses', 'Tips for guide / driver', 'Travel insurance'],
      eligibility: 'Suitable for all ages. Children under 5 must be accompanied by an adult at all times.',
      additionalInfo: 'Wear comfortable closed-toe shoes. The mud volcano site is uneven terrain. Sun protection recommended in summer.',
      prohibitions: ['No drone photography inside the park', 'No food/drink inside the museum', 'No climbing on rock carvings'],
      limitations: ['Not wheelchair accessible at volcano site', 'Pregnant women advised against off-road jeep', 'Maximum 6 pax per private tour'],
      confirmationRequired: 'Re-confirm hotel pickup 24h before tour start by calling the operator on +994 50 555 4477.',
      cancellationTerms: '• Free cancellation up to 48h before tour start.\n• 50% charge if cancelled 24-48h prior.\n• 100% charge for no-show or cancellation within 24h.',
      howToRedeem: 'Present this voucher (printed or on mobile) to the guide at hotel pickup. Guide will tick off the partner reference on their list.',
      howToGetThere: 'Tour starts with hotel lobby pickup at 09:00. If you arrive late, please call +994 50 555 4477 — operator can attempt to re-route the driver, but tour timing cannot be extended.',
      meetingPoint: 'Hotel lobby of your accommodation in central Baku (pickup at 09:00)',
      hotline: '+994 50 555 4477',
      emergencyContact: '+994 12 999 0011',
      operator: 'Azerbaijan Heritage Tours LLC',
      operatorPhone: '+994 50 555 4477',
      operatorEmail: 'ops@az-heritage.az',
      guideName: 'Elnur Mammadov',
      guidePhone: '+994 50 555 2233',
      contactDetails: [
        { label: 'WhatsApp 24×7', value: '+994 50 555 4477' },
        { label: 'Operator Email', value: 'ops@az-heritage.az' },
        { label: 'Website', value: 'https://az-heritage.az' },
      ],
      terms: 'By using this voucher, the guest agrees to abide by the rules of the Gobustan National Park and the Azerbaijan Heritage Tours LLC code of conduct. Photography for personal use is permitted in designated areas only. Commercial photography requires advance written permission from the park authority. The operator reserves the right to modify the tour itinerary due to weather or safety conditions without prior notice. In the unlikely event of cancellation by the operator, a full refund will be issued. The operator is not liable for loss of personal belongings during the tour.',
    },
    publicUrl: 'https://travcations.com/voucher/VCH-ACT-DEMO-001',
  },
  transfer: {
    voucherId: 'VCH-TRF-DEMO-001',
    type: 'transfer',
    status: 'confirmed',
    guestName: 'Rahul & Priya Sharma',
    destination: 'Baku Arrival',
    transfer: {
      pickupLocation: 'Heydar Aliyev International Airport (GYD)',
      pickupDateTime: '12 Feb 2026 · 09:45',
      dropLocation: 'Four Seasons Hotel Baku',
      dropDateTime: '12 Feb 2026 · 10:30',
      vehicleType: 'Mercedes-Benz E-Class (Sedan)',
      passengers: 2,
      flightRef: 'J2-76',
      driverName: 'Rashad Aliyev',
      driverPhone: '+994 50 555 2233',
    },
    publicUrl: 'https://travcations.com/voucher/VCH-TRF-DEMO-001',
  },
  itinerary: {
    voucherId: 'VCH-ITN-DEMO-001',
    type: 'itinerary',
    status: 'confirmed',
    guestName: 'Rahul & Priya Sharma',
    destination: 'Azerbaijan',
    itinerary: {
      destination: 'Baku & Gabala',
      startDate: '12 Feb 2026',
      endDate: '17 Feb 2026',
      duration: '5 Nights / 6 Days',
      travellers: '2 Adults',
      reference: 'TRV-ITN-8876',
      days: [
        { title: 'Day 1 · Arrival in Baku', description: 'Airport pickup, hotel check-in at Four Seasons, evening walk along Baku Boulevard.' },
        { title: 'Day 2 · Old City + Flame Towers', description: 'Guided walking tour of Icherisheher, Maiden Tower, Palace of the Shirvanshahs. Flame Towers light show at night.' },
        { title: 'Day 3 · Gobustan + Mud Volcanoes', description: 'Full-day tour to Gobustan National Park. Lunch at local restaurant. Mud volcanoes UNESCO site.' },
        { title: 'Day 4 · Drive to Gabala', description: 'Scenic 3-hour drive north. Check-in at mountain resort. Gabaland amusement park in the evening.' },
        { title: 'Day 5 · Tufandag Cable Car', description: 'Cable car to summit for panoramic Caucasus views. Nohur Lake visit. Return to Baku.' },
        { title: 'Day 6 · Departure', description: 'Breakfast. Airport drop-off for evening flight.' },
      ],
    },
    publicUrl: 'https://travcations.com/voucher/VCH-ITN-DEMO-001',
  },
};

export default function DemoVoucher() {
  const { type = 'hotel' } = useParams();
  const [company, setCompany] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/company`)
      .then((r) => setCompany(r.data))
      .catch(() => setCompany({
        companyName: 'Travcations',
        tagline: "Let's Explore to Experience",
        logoUrl: '/api/uploads/travcations-logo.png',
        primaryColor: '#34D0CB',
        accentColor: '#595A5B',
      }));
  }, []);

  const sample = SAMPLES[type] || SAMPLES.hotel;

  if (!company) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading demo…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="max-w-5xl mx-auto mb-6 px-6">
        <h1 className="text-2xl font-bold text-slate-900">Voucher Design Preview</h1>
        <p className="text-sm text-slate-500 mt-1">Sample {type} voucher rendered with live Travcations branding. Swap the URL path to preview other types:</p>
        <div className="mt-3 flex gap-2 flex-wrap">
          {['hotel', 'flight', 'activity', 'transfer', 'itinerary'].map((t) => (
            <a
              key={t}
              href={`/demo/voucher/${t}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${
                t === type ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-teal-400'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </a>
          ))}
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6">
        <div className="shadow-2xl mx-auto bg-white">
          <BrandedVoucherLayout
            voucher={sample}
            company={company}
            qrValue={sample.publicUrl}
            forPdf={false}
          >
            {sample.type === 'flight' && <FlightVoucherBody voucher={sample} company={company} />}
            {sample.type === 'hotel' && <HotelVoucherBody voucher={sample} company={company} />}
            {sample.type === 'activity' && <ActivityVoucherBody voucher={sample} company={company} />}
            {sample.type === 'transfer' && <TransferVoucherBody voucher={sample} company={company} />}
            {sample.type === 'itinerary' && <ItineraryVoucherBody voucher={sample} company={company} />}
          </BrandedVoucherLayout>
        </div>
      </div>
    </div>
  );
}
